import { ERequestStatus } from "../interfaces";
import { SPLOOT_BADGE_COLOR } from "../themes/badge";
const { IN_PROGRESS, CANCELLED, ASSIGNED } = ERequestStatus;

export const SELECTED_TEXT_COLOR = "text-white";

/**
 * Enum representing the type of a request.
 *
 * This enum is used to categorize requests based on their purpose:
 * - NEW: Request is for a new subscription or session
 * - RESCHEDULE: Request is for rescheduling existing sessions
 * - REPLACE: Request is for replacing a partner for existing sessions
 */
export enum ESessionAssignmentRequestType {
  NEW = "NEW",
  RESCHEDULE = "RESCHEDULE",
  REPLACE = "REPLACE",
}

export enum ESessionAssignmentRequestStatus {
  INITIATED = "INITIATED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  EXPIRED = "EXPIRED",
  CANCELLED = "CANCELLED",
}

/**
 * Status enum for session assignment request sessions junction table
 */
export enum ESessionAssignmentRequestSessionStatus {
  INITIALIZED = "INITIALIZED",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

/**
 * Enum representing the scope of a request.
 *
 * This enum is used to categorize requests based on what they're for:
 * - SUBSCRIPTION: Request is for a subscription (multiple sessions over time)
 * - SESSION: Request is for individual sessions or a group of sessions
 */
export enum ESessionAssignmentRequestScope {
  SUBSCRIPTION = "SUBSCRIPTION",
  SESSION = "SESSION",
}

export enum ESessionAssignmentRequestCycleStatus {
  INITIATED = "INITIATED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  EXPIRED = "EXPIRED",
  CANCELLED = "CANCELLED",
}

export enum ESessionAssignmentRequestAttemptStatus {
  DRAFT = "DRAFT",
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  IGNORED = "IGNORED",
  EXPIRED = "EXPIRED",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED",
}

/**
 * Enum representing the stages of the request assignment process.
 * This matches the backend ProcessStage enum.
 */
export enum EProcessStage {
  INITIALIZED = "INITIALIZED",
  REQUEST_VALIDATED = "REQUEST_VALIDATED",
  SESSIONS_FOUND = "SESSIONS_FOUND",
  PARTNERS_FOUND = "PARTNERS_FOUND",
  PARTNER_DETAILS_FETCHED = "PARTNER_DETAILS_FETCHED",
  AVAILABILITY_COMPUTED = "AVAILABILITY_COMPUTED",
  PARTNERS_RANKED = "PARTNERS_RANKED",
  OFFERABLE_SESSIONS_FOUND = "OFFERABLE_SESSIONS_FOUND",
  PARTNER_SELECTED = "PARTNER_SELECTED",
  ACTIVATED = "ACTIVATED",
  EXPIRED = "EXPIRED",
  FAILED_NO_SESSIONS = "FAILED_NO_SESSIONS",
  FAILED_NO_PARTNERS_FOUND = "FAILED_NO_PARTNERS_FOUND",
  FAILED_NO_AVAILABLE_PARTNERS = "FAILED_NO_AVAILABLE_PARTNERS",
  FAILED_NO_RANKED_PARTNERS = "FAILED_NO_RANKED_PARTNERS",
  FAILED_NO_ELIGIBLE_PARTNERS_CYCLE_EXHAUSTED = "FAILED_NO_ELIGIBLE_PARTNERS_CYCLE_EXHAUSTED",
  FAILED_NO_OFFERABLE_SESSIONS = "FAILED_NO_OFFERABLE_SESSIONS",
  FAILED_NO_PARTNER_WITH_OFFERABLE_SESSIONS = "FAILED_NO_PARTNER_WITH_OFFERABLE_SESSIONS",
  COMPLETED_PARTNER_ACCEPTED = "COMPLETED_PARTNER_ACCEPTED",
  FAILED_PARTNER_REJECTED = "FAILED_PARTNER_REJECTED",
  FAILED_PARTNER_IGNORED = "FAILED_PARTNER_IGNORED",
}

/**
 * Notification channels supported for session assignment requests
 */
export enum ENotificationChannel {
  PUSH = "PUSH",
  SMS = "SMS",
  EMAIL = "EMAIL",
  WHATS_APP = "WHATS_APP",
}

/**
 * Notification attempt status
 */
export enum ENotificationAttemptStatus {
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

/**
 * Enum representing the combination of request type and scope
 * Format: "{type}-{scope}"
 */
export enum ESessionAssignmentRequestTypeScope {
  NEW_SUBSCRIPTION = "New-Subscription",
  NEW_SESSION = "New-Session",
  RESCHEDULE_SUBSCRIPTION = "Reschedule-Subscription",
  RESCHEDULE_SESSION = "Reschedule-Session",
  REPLACE_SUBSCRIPTION = "Replace-Subscription",
  REPLACE_SESSION = "Replace-Session",
}

export const STATUS_BADGE_COLORS = {
  [IN_PROGRESS]: SPLOOT_BADGE_COLOR.YELLOW,
  [CANCELLED]: SPLOOT_BADGE_COLOR.RED,
  [ASSIGNED]: SPLOOT_BADGE_COLOR.GREEN,
};

/**
 * Badge colors for session assignment request attempt statuses
 * Maps attempt statuses directly to badge colors using ESessionAssignmentRequestAttemptStatus enum
 */
export const SESSION_ASSIGNMENT_REQUEST_ATTEMPT_STATUS_BADGE_COLORS = {
  [ESessionAssignmentRequestAttemptStatus.ACCEPTED]: SPLOOT_BADGE_COLOR.GREEN,
  [ESessionAssignmentRequestAttemptStatus.PENDING]: SPLOOT_BADGE_COLOR.YELLOW,
  [ESessionAssignmentRequestAttemptStatus.REJECTED]: SPLOOT_BADGE_COLOR.RED,
  [ESessionAssignmentRequestAttemptStatus.IGNORED]: SPLOOT_BADGE_COLOR.ORANGE,
  [ESessionAssignmentRequestAttemptStatus.EXPIRED]:
    SPLOOT_BADGE_COLOR.LIGHT_GRAY,
  [ESessionAssignmentRequestAttemptStatus.CANCELLED]: SPLOOT_BADGE_COLOR.RED,
  [ESessionAssignmentRequestAttemptStatus.DRAFT]: SPLOOT_BADGE_COLOR.NEUTRAL,
  [ESessionAssignmentRequestAttemptStatus.FAILED]: SPLOOT_BADGE_COLOR.RED,
};

export const SESSION_ASSIGNMENT_REQUEST_STATUS_BADGE_COLORS = {
  [ESessionAssignmentRequestStatus.INITIATED]: SPLOOT_BADGE_COLOR.GRAY,
  [ESessionAssignmentRequestStatus.IN_PROGRESS]: SPLOOT_BADGE_COLOR.YELLOW,
  [ESessionAssignmentRequestStatus.COMPLETED]: SPLOOT_BADGE_COLOR.GREEN,
  [ESessionAssignmentRequestStatus.FAILED]: SPLOOT_BADGE_COLOR.RED,
  [ESessionAssignmentRequestStatus.EXPIRED]: SPLOOT_BADGE_COLOR.LIGHT_GRAY,
  [ESessionAssignmentRequestStatus.CANCELLED]: SPLOOT_BADGE_COLOR.RED,
};

/**
 * Display labels for session assignment request statuses
 * Maps enum values to UI-friendly labels
 */
export const SESSION_ASSIGNMENT_REQUEST_STATUS_LABELS = {
  [ESessionAssignmentRequestStatus.INITIATED]: "INITIATED",
  [ESessionAssignmentRequestStatus.IN_PROGRESS]: "IN_PROGRESS",
  [ESessionAssignmentRequestStatus.COMPLETED]: "ACCEPTED",
  [ESessionAssignmentRequestStatus.FAILED]: "FAILED",
  [ESessionAssignmentRequestStatus.EXPIRED]: "EXPIRED",
  [ESessionAssignmentRequestStatus.CANCELLED]: "CANCELLED",
} as const;

/**
 * Badge colors for notification attempt statuses
 */
export const NOTIFICATION_ATTEMPT_STATUS_BADGE_COLORS = {
  [ENotificationAttemptStatus.SUCCESS]: SPLOOT_BADGE_COLOR.GREEN,
  [ENotificationAttemptStatus.FAILED]: SPLOOT_BADGE_COLOR.RED,
};

/**
 * Badge colors for session assignment request type-scope combinations
 */
export const SESSION_ASSIGNMENT_REQUEST_TYPE_SCOPE_BADGE_COLORS = {
  [ESessionAssignmentRequestTypeScope.NEW_SUBSCRIPTION]:
    SPLOOT_BADGE_COLOR.NEUTRAL,
  [ESessionAssignmentRequestTypeScope.NEW_SESSION]: SPLOOT_BADGE_COLOR.NEUTRAL,
  [ESessionAssignmentRequestTypeScope.RESCHEDULE_SUBSCRIPTION]:
    SPLOOT_BADGE_COLOR.ORANGE,
  [ESessionAssignmentRequestTypeScope.RESCHEDULE_SESSION]:
    SPLOOT_BADGE_COLOR.ORANGE,
  [ESessionAssignmentRequestTypeScope.REPLACE_SUBSCRIPTION]:
    SPLOOT_BADGE_COLOR.GREEN,
  [ESessionAssignmentRequestTypeScope.REPLACE_SESSION]:
    SPLOOT_BADGE_COLOR.GREEN,
} as const;

/**
 * Display text constants for session assignment request
 */
export const SESSION_ASSIGNMENT_REQUEST_DISPLAY = {
  ACTIONS: {
    VIEW_REQUEST_SESSIONS: "View Request Sessions",
    VIEW_SUBSCRIPTION: "View Subscription",
    FORCE_STOP: "Force Stop",
  },
  HEADER: {
    TITLE: "Request Details",
  },
  SUBSCRIPTION_INFO: {
    TITLE: "Subscription Info",
  },
  SUMMARY: {
    PARTNER_SINGULAR: "Partner",
    PARTNER_PLURAL: "Partners",
    ATTEMPT_SINGULAR: "attempt",
    ATTEMPT_PLURAL: "attempts",
    SEPARATOR: " • ",
  },
  REQUEST_CYCLE: {
    TITLE: "Request Cycle",
    ATTEMPT_SINGULAR: "Attempt",
    ATTEMPT_PLURAL: "Attempts",
    SESSION_SINGULAR: "session",
    SESSION_PLURAL: "sessions",
    NOT_AVAILABLE: "N/A",
  },
} as const;

/**
 * Constants for request cycle header component styling and layout
 * Note: Tailwind classes must be complete class names (not template literals) for proper tree-shaking
 */
export const REQUEST_CYCLE_HEADER_CONFIG = {
  CYCLE_NUMBER_BADGE: {
    BACKGROUND_COLOR: "bg-blue-100",
    TEXT_COLOR: "text-blue-500",
  },
  SEPARATOR: {
    COLOR: "text-neutral-400",
    CHARACTER: "•",
  },
} as const;
